import React, { useState, useEffect, useCallback, ForwardedRef, forwardRef, useImperativeHandle, useMemo } from 'react'
import { useLazyQuery } from '@apollo/client';
import { IOrg } from '../../../../types/org';
import { GET_INFOGO_POSITIONS } from '../../../../graphql/infogo-gql';
import PositionItem from './position-item';
import { IInfoGoPosition, IPresentationInput } from '../../../../types/infogo';
import { PresentationObject } from '../../../../types/constants';
import SpinnerOverlay from '../../../common/spinner/spinner';
import { DndContext, closestCenter, DragOverlay, useSensors, useSensor, MouseSensor, KeyboardSensor } from '@dnd-kit/core';
import { arrayMove } from '../../../../utils/commons';
import { SortableContext, verticalListSortingStrategy, sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import '../infogo-config.scss';
import useAuth from '../../../../hook/useAuth';

export type PositionConfigHandler = {
    getPositionConfig: () => IPresentationInput[];
    reset: () => void;
    hasChange: () => boolean;
    hasNewSuppress: () => boolean;
}

type InfoGoConfigProps = {
    org?: IOrg;
    onChange: () => void;
}

const PositionConfig = forwardRef<PositionConfigHandler, InfoGoConfigProps>((props: InfoGoConfigProps, ref:ForwardedRef<any>) => {
    const { org, onChange } = props
    const { isFrenchTranslator } = useAuth()
    const [ positions, setPositions ] = useState<IInfoGoPosition[]>([])

    const [ suppressedIds, setSuppressedIds ] = useState<string[]>([])
    const [ intranetIds, setIntranetIds ] = useState<string[]>([])

    const [ prevIntranet, setPrevIntranet ] = useState<string[]>([])
    const [ prevSuppress, setPrevSuppress ] = useState<string[]>([])
    const [ prevSequence, setPrevSequence ] = useState<string[]>([])

    const [activeId, setActiveId] = useState<string | null>(null)

    const [getPositions, { loading, data }] = useLazyQuery(GET_INFOGO_POSITIONS, {
        fetchPolicy: 'network-only',
        onCompleted: (data) => {
            if (data?.infoGoPositions) {
                const intranetVls = data.infoGoPositions.filter((e: IInfoGoPosition) => e.intranet).map((e: IInfoGoPosition) => e.relationshipId)
                setPrevIntranet(intranetVls)
                setIntranetIds(intranetVls)
                const suppressVls = data.infoGoPositions.filter((e: IInfoGoPosition) => e.suppress).map((e: IInfoGoPosition) => e.relationshipId)
                setPrevSuppress(suppressVls)
                setSuppressedIds(suppressVls)
                const sequenceVls = data.infoGoPositions.map((e: IInfoGoPosition) => e.relationshipId)
                setPrevSequence(sequenceVls)
            }
            setPositions(data?.infoGoPositions ? [...data.infoGoPositions] : [])
        }
    })

    useImperativeHandle(ref, () => ({
        getPositionConfig() {
            let allIds = data?.infoGoPositions.map((e: IInfoGoPosition) => e.relationshipId)
            let input: IPresentationInput[] = positions.map((item: IInfoGoPosition, index: number) => {
                return {
                    typeName: PresentationObject.GENERAL_RELATIONSHIP,
                    objectId: item.relationshipId,
                    suppress: suppressedIds.includes(item.relationshipId) ? true: false,
                    intranet: intranetIds.includes(item.relationshipId) ? true: false,
                    sequence: index + 1
                }
            })
            return input
        },
        reset() {
            onReset()
        },
        hasChange() {
            return reOrdered() || hasSuppressChange() || hasIntranetChange()
        },
        hasNewSuppress() {
            return hasNewSuppressChange()
        }
    }),[data, suppressedIds, intranetIds, positions])

    useEffect(() => {
        if (org) {
            getPositions({ variables: { orgId: org.id } })
        }
    }, [org])

    const managementIds = useMemo(
        () => positions.filter((p: IInfoGoPosition) => p.managementIndicator).map((p: IInfoGoPosition) => p.relationshipId),
        [positions]
    )

    const sensors = useSensors(
        useSensor(MouseSensor, {
            activationConstraint: {
                distance: 8
            }
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates
        })
    )

    const onReset = () => {
        setPositions([...data?.infoGoPositions])
        setSuppressedIds(prevSuppress)
        setIntranetIds(prevIntranet)
        setTimeout(()=>onChange())
    }

    const onSuppressCheck = (event: any, id: string) => {
        if (event.target.checked) {
            setSuppressedIds([...suppressedIds, id])
        } else {
            setSuppressedIds(suppressedIds.filter(e => e != id))
        }
        setTimeout(()=>onChange())
    }

    const onIntranetCheck = (event: any, id: string) => {
        if (event.target.checked) {
            setIntranetIds([...intranetIds, id])
        } else {
            setIntranetIds(intranetIds.filter(e => e != id))
        }
        setTimeout(()=>onChange())
    }

    const reOrdered = (): boolean => {
        const newSequence = positions.map((e: IInfoGoPosition) => e.relationshipId)
        if (prevSequence.length > 0) {
            return prevSequence.some((value, index) => value !== newSequence[index])
        }
        return false;
    }

    const hasSuppressChange = (): boolean => {
        if (prevSuppress.length !== suppressedIds.length) {
            return true
        }
        if (prevSuppress.length > 0) {
            return prevSuppress.some((value) => !suppressedIds.includes(value))
        }
        return false
    }

    const hasNewSuppressChange = (): boolean => {
        if (suppressedIds.length > prevSuppress.length) {
            return true
        }
        if (suppressedIds.length > 0) {
            return suppressedIds.some((value) => !prevSuppress.includes(value))
        }
        return false
    }

    const hasIntranetChange = (): boolean => {
        if (prevIntranet.length !== intranetIds.length) {
            return true
        }
        if (prevIntranet.length > 0) {
            return prevIntranet.some((value) => !intranetIds.includes(value))
        }
        return false
    }

    const handleDragStart = (event: any) => {
        const { active } = event
        if (active && active.id) {
            setActiveId(active.id)
        }
    }

    const handleDragEnd = (event: any) => {
        const { active, over } = event
        if (over && active.id !== over.id && !managementIds.includes(over.id)) {
            setPositions((positionList: IInfoGoPosition[]) => {
                const oldIndex = positionList.findIndex((item) => item.relationshipId === active.id);
                const newIndex = positionList.findIndex((item) => item.relationshipId === over.id);
                return arrayMove(positionList, oldIndex, newIndex);
            })
        }
        setActiveId(null)
        setTimeout(()=>onChange())
    }

    const getOverlayPosition = () => {
        return positions.find((pos: IInfoGoPosition) => pos.relationshipId === activeId) as IInfoGoPosition
    }

    return (
        <div>
            {loading && <SpinnerOverlay />}

            {
                positions.length === 0
                ?
                <div style={{ textAlign: 'center' }}>No positions or roles found.</div>
                :
                <>
                    <div className="infogo-grid-row grid-header">
                        <div className="infogo-pos-col1">
                        </div>
                        <div className="infogo-pos-col2">
                            Individual Name - Position/Role
                        </div>
                        <div  className="infogo-pos-col3">
                            Intranet Only?
                        </div>
                        <div className="infogo-pos-col4">
                            Suppressed?
                        </div>
                    </div>
                    <div>
                       { !isFrenchTranslator && <DndContext sensors={sensors}
                            collisionDetection={closestCenter}
                            onDragStart={handleDragStart}
                            onDragEnd={handleDragEnd}
                            onDragCancel={() => setActiveId(null)}>
                            <SortableContext strategy={verticalListSortingStrategy} items={positions.map((pos: IInfoGoPosition) => pos.relationshipId)}>
                                <div>
                                    {positions.map((pos: IInfoGoPosition) => (
                                        <PositionItem
                                            key={pos.relationshipId}
                                            id={pos.relationshipId}
                                            shareCloned={!!org?.details?.shareLinkId}
                                            suppressed={suppressedIds.includes(pos.relationshipId)}
                                            intranet={intranetIds.includes(pos.relationshipId)}
                                            onIntranetCheck={event => onIntranetCheck(event, pos.relationshipId)}
                                            onSuppressCheck={event => onSuppressCheck(event, pos.relationshipId)}
                                            position={pos}
                                            visible={pos.relationshipId !== activeId}/>
                                    ))}
                                </div>
                            </SortableContext>
                            <DragOverlay>
                                {activeId &&
                                    <PositionItem
                                        id={activeId}
                                        position={getOverlayPosition()}
                                        shareCloned={!!org?.details?.shareLinkId}
                                        suppressed={suppressedIds.includes(activeId)}
                                        intranet={intranetIds.includes(activeId)}
                                        onSuppressCheck={()=>{}}
                                        onIntranetCheck={()=>{}}
                                        visible
                                        isDragging />
                                }
                            </DragOverlay>
                        </DndContext>}
                        {isFrenchTranslator && <div>
                            {positions.map((pos: IInfoGoPosition) => (
                                        <PositionItem
                                            key={pos.relationshipId}
                                            id={pos.relationshipId}
                                            shareCloned={!!org?.details?.shareLinkId}
                                            suppressed={suppressedIds.includes(pos.relationshipId)}
                                            intranet={intranetIds.includes(pos.relationshipId)}
                                            onIntranetCheck={event => onIntranetCheck(event, pos.relationshipId)}
                                            onSuppressCheck={event => onSuppressCheck(event, pos.relationshipId)}
                                            position={pos}
                                            visible={pos.relationshipId !== activeId}/>
                            ))}
                        </div>
                        }
                     </div>
                </>
            }
        </div>
    )
})

export default PositionConfig
